import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import Button from '@/components/Button';
import Dialog, {
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from '@/components/Dialog';
import ErrorMessage from '@/components/ErrorMessage';
import ScrollArea from '@/components/ScrollArea';
import VisuallyHidden from '@/components/VisuallyHidden';
import Input from '@/components/form/Input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/form/Select';
import FormTextArea from '@/components/form/TextArea';
import contactModel from '@/models/contact';
import { CONSTANTS } from '@/pages/Contact/Form';
import ImageDropdown from '@/pages/Contact/ImageDropdown';
import { useReasons } from '@/pages/Contact/hooks';
import { setIsModalContactOpen } from '@/store/site';
import { cn, showSuccess } from '@/utils';

function ModalContactPrivate() {
	const [errorMessage, setErrorMessage] = useState('');
	const dispatch = useDispatch();
	const open = useSelector(state => state.site.isModalContactOpen);
	const reasonOptions = useReasons();
	const { t } = useTranslation();

	const [loading, setLoading] = useState(false);

	const onOpenChange = bool => {
		dispatch(setIsModalContactOpen(bool));
	};

	const validationSchema = Yup.object({
		reason: Yup.string().required(),
		subject: Yup.string().max(100).required(),
		message: Yup.string().max(500).required(),
		file: Yup.mixed()
			.optional()
			.nullable()
			.test({
				name: 'fileSize',
				message: t('contact.form.invalidFileSize', {
					size: CONSTANTS.MAX_FILE_SIZE_MB
				}),
				test: file => {
					if (!file || typeof file === 'string') {
						return true;
					}
					const fileSizeInMb = file.size / (1024 * 1024);

					return fileSizeInMb <= CONSTANTS.MAX_FILE_SIZE_MB;
				}
			})
			.test('fileFormat', t('contact.form.invalidFileFormat'), value => {
				return (
					!value ||
					(value && CONSTANTS.ACCEPTED_TYPES.includes(value.type))
				);
			})
	});

	const methods = useForm({
		resolver: yupResolver(validationSchema),
		mode: 'onChange',
		defaultValues: {
			reason: '',
			subject: '',
			message: ''
		}
	});

	async function onSubmit(formValues) {
		setErrorMessage('');
		setLoading(true);

		try {
			await contactModel.postFormPrivate(formValues);
			showSuccess(t('contact.toast.sendInquirySuccess'));
			onOpenChange(false);
		} catch (error) {
			setErrorMessage(t('errors.generic'));
		}
		setLoading(false);
	}

	useEffect(() => {
		if (!open) {
			methods.reset();
			setErrorMessage('');
		}
	}, [open]);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="w-[320px] p-0 md:w-[38rem]">
				<FormProvider {...methods}>
					<form
						onSubmit={methods.handleSubmit(onSubmit)}
						className="flex w-full flex-col items-center justify-center rounded-lg bg-white"
					>
						<DialogHeader className="flex h-12 w-full flex-row items-center justify-start border-b border-grey-200 px-4">
							<DialogTitle className="font-title text-sm text-xs font-semibold leading-5 leading-6 md:text-sm md:leading-6">
								{t('contact.private.title')}
							</DialogTitle>
							<VisuallyHidden asChild>
								<DialogDescription>
									{t('contact.private.description')}
								</DialogDescription>
							</VisuallyHidden>
						</DialogHeader>

						<ScrollArea
							className="max-h-[calc(100vh-8rem)] w-full"
							maxHeight="100%"
						>
							<div className="flex max-h-[70vh] w-full flex-col gap-y-4 p-4 lg:pt-6">
								<p className="text-xs font-semibold leading-5 text-grey-500 md:text-sm md:leading-6 lg:mb-2">
									{t('contact.private.description')}
								</p>

								<Select
									name="reason"
									required
									label={`${t('contact.form.reason')}`}
								>
									<SelectTrigger
										size="lg"
										className="rounded"
										aria-label={t('contact.form.reason')}
									>
										<SelectValue
											placeholder={`${t('contact.form.placeholder.reason')}`}
										/>
									</SelectTrigger>
									<SelectContent className="border-0 border-grey-200 bg-white">
										{reasonOptions.map((res, index) => {
											return (
												<SelectItem key={index} value={res.value}>
													{res.label}
												</SelectItem>
											);
										})}
									</SelectContent>
								</Select>

								<Input
									label={`${t('contact.form.subject')} *`}
									name="subject"
									placeholder={`${t('contact.form.placeholder.subject')}`}
									size="lg"
								/>

								<FormTextArea
									className="min-h-[100px]"
									name="message"
									size="lg"
									label={`${t('contact.form.message')} *`}
									placeholder={`${t('contact.form.placeholder.message')}`}
								/>

								<ImageDropdown
									setFormFile={file => methods.setValue('file', file)}
								/>
							</div>

							<ErrorMessage
								className={cn(
									'mt-0 px-4 text-center',
									!!errorMessage && 'pb-2'
								)}
							>
								{errorMessage}
							</ErrorMessage>
						</ScrollArea>

						<DialogFooter className="w-full">
							<Button
								className="w-full"
								onClick={() => onOpenChange(false)}
								variant="outline"
							>
								{t('common.cancel')}
							</Button>
							<Button
								aria-label={`${t('contact.form.button')} ${t('labels.form')}`}
								className="w-full"
								loading={loading}
								type="submit"
							>
								{t('contact.form.button')}
							</Button>
						</DialogFooter>
					</form>
				</FormProvider>
			</DialogContent>
		</Dialog>
	);
}

export default ModalContactPrivate;
